"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="222481" className="hidden md:flex gap-4">
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default uppercase text-xs font-medium text-foreground hover:text-primary transition-colors tracking-widest">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-md border-b border-border md:hidden shadow-lg">
      <div className="container mx-auto px-4 py-6 flex flex-col gap-4">
        {loading ? <div className="flex flex-col gap-4">
            <div className="h-8 w-32 bg-muted animate-pulse rounded" />
            <div className="h-8 w-32 bg-muted animate-pulse rounded" />
          </div> : <nav className="flex flex-col gap-4">
            {menuItems.map((item, i) => <div key={item.id} data-index={i}>
                <Link to={item.href || "#"} className="font-default uppercase text-lg text-foreground hover:text-primary transition-colors block py-2" onClick={onClose}>
                  {item.label}
                </Link>
              </div>)}
          </nav>}
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-lg border-b border-border shadow-sm" : "border-b border-transparent"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 z-10">
          <Link to="/">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/ChatGPT Image 5. Mai 2026, 11_43_26.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/ChatGPT Image 5. Mai 2026, 11_43_26.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642178/img/ChatGPT Image 5. Mai 2026, 11_43_26.png-wvc-sizes" alt="Baumpflege Straesser" className="h-28 md:h-40 w-auto object-contain bg-transparent" loading="lazy" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-center">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-4 z-10">
          {}
          <div className="flex items-center gap-3 bg-destructive/5 border border-destructive/20 px-4 py-1.5 rounded-full">
            <Link to="tel:01639054671" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <div className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive"></span>
              </div>
              <span className="text-sm font-mono font-bold text-destructive">01639054671</span>
            </Link>
            <div className="w-px h-3 bg-destructive/20" />
            <span className="text-[10px] font-default font-bold uppercase tracking-wider text-foreground">
              Kostenloses Angebot diese woche
            </span>
          </div>

          

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent hover:text-accent-foreground" onClick={toggleMobileMenu} aria-label="Toggle Menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </section>;
}